import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  TextInput,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  Image,
  ActivityIndicator,
  StatusBar,
  Alert,
  Modal,
  KeyboardAvoidingView,
  Platform,
  RefreshControl,
  Animated,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import portfolioDatabase from '../../services/PortfolioDatabase';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

export default function AllProjectsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_allprojectsscreen`;
    apiFetch('/api/data/applications?sort=created_at&order=desc&limit=20').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const navigation = useNavigation();

  // Data states
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [sortBy, setSortBy] = useState('date');
  const [showFilters, setShowFilters] = useState(false);

  // Edit/Add states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMode, setEditMode] = useState('add');
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Animation
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Categories and statuses
  const categories = ['All', 'Infrastructure', 'Energy', 'Healthcare', 'Transportation', 'Residential', 'Commercial'];
  const statuses = ['All', 'Planning', 'In Progress', 'Completed', 'On Hold', 'Cancelled'];

  // Load data when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      loadProjects();
    }, [])
  );

  // Set up event listeners
  useEffect(() => {
    const setupEventListeners = () => {
      portfolioDatabase.subscribe('project_added', (project) => {
        setProjects(prev => [project, ...prev]);
      });

      portfolioDatabase.subscribe('project_updated', (project) => {
        setProjects(prev => prev.map(p => p.id === project.id ? project : p));
      });

      portfolioDatabase.subscribe('project_deleted', ({ id }) => {
        setProjects(prev => prev.filter(p => p.id !== id));
      });
    };

    if (portfolioDatabase.initialized) {
      setupEventListeners();
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Apply filters and search
  useEffect(() => {
    applyFiltersAndSearch();
  }, [projects, searchQuery, selectedCategory, selectedStatus, sortBy]);

  // Start animations
  useEffect(() => {
    if (!isLoading) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }).start();
    }
  }, [isLoading]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      
      // Initialize database if not already done
      if (!portfolioDatabase.initialized) {
        await portfolioDatabase.initialize();
      }

      const projectsData = await portfolioDatabase.getProjects();
      setProjects(projectsData || []);
      
    } catch (error) {
      console.error('❌ Error loading projects:', error);
      Alert.alert('Error', 'Failed to load projects. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...projects];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(project =>
        String(project.title ?? '').toLowerCase().includes(query) ||
        String(project.description ?? '').toLowerCase().includes(query) ||
        String(project.client ?? '').toLowerCase().includes(query) ||
        (project.technologies && project.technologies.some(tech => 
          tech.toLowerCase().includes(query)
        ))
      );
    }

    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(project => 
        project.category === selectedCategory
      );
    }

    // Apply status filter
    if (selectedStatus !== 'All') {
      filtered = filtered.filter(project => 
        project.status === selectedStatus
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'client':
          return a.client.localeCompare(b.client);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'date':
        default:
          return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      }
    });

    setFilteredProjects(filtered);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadProjects();
    } catch (error) {
      console.error('❌ Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Form validation
  const validateForm = (data) => {
    const errors = {};

    if (!data.title?.trim()) errors.title = 'Title is required';
    if (!data.description?.trim()) errors.description = 'Description is required';
    if (!data.client?.trim()) errors.client = 'Client is required';
    if (!data.status?.trim()) errors.status = 'Status is required';
    if (!data.date?.trim()) errors.date = 'Date is required';

    return errors;
  };

  // Handle add project
  const handleAddProject = () => {
    setEditMode('add');
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      client: '',
      status: 'Planning',
      date: '',
      budget: '',
      teamSize: '',
      category: 'Infrastructure',
      technologies: [],
      achievements: [],
      image: 'https://via.placeholder.com/300/0e4e96/FFFFFF?text=Project'
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle edit project
  const handleEditProject = (project) => {
    setEditMode('edit');
    setEditingProject(project);
    setFormData({ ...project });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle delete project
  const handleDeleteProject = (project) => {
    Alert.alert(
      'Delete Project',
      `Are you sure you want to delete "${project.title}"? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const success = await portfolioDatabase.deleteProject(project.id);
              if (success) {
                Alert.alert('Success', 'Project deleted successfully');
              } else {
                Alert.alert('Error', 'Failed to delete project');
              }
            } catch (error) {
              console.error('❌ Delete error:', error);
              Alert.alert('Error', 'Failed to delete project');
            }
          }
        }
      ]
    );
  };

  // Handle save project
  const handleSaveProject = async () => {
    const errors = validateForm(formData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      Alert.alert('Validation Error', 'Please fix the errors in the form');
      return;
    }

    try {
      let result = null;

      if (editMode === 'add') {
        result = await portfolioDatabase.addProject(formData);
      } else {
        result = await portfolioDatabase.updateProject(editingProject.id, formData);
      }

      if (result) {
        setShowEditModal(false);
        Alert.alert('Success', `Project ${editMode === 'add' ? 'added' : 'updated'} successfully`);
      } else {
        Alert.alert('Error', `Failed to ${editMode} project`);
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      Alert.alert('Error', `Failed to ${editMode} project`);
    }
  };

  // Render project card
  const renderProjectCard = ({ item: project }) => (
    <TouchableOpacity
      style={styles.projectCard}
      onPress={() => {
        // Navigate to project details or show details modal
        Alert.alert(
          project.title,
          project.description,
          [
            { text: 'Edit', onPress: () => handleEditProject(project) },
            { text: 'Delete', onPress: () => handleDeleteProject(project), style: 'destructive' },
            { text: 'Close', style: 'cancel' }
          ]
        );
      }}
      onLongPress={() => {
        Alert.alert(
          'Project Options',
          `What would you like to do with "${project.title}"?`,
          [
            { text: 'Edit', onPress: () => handleEditProject(project) },
            { text: 'Delete', onPress: () => handleDeleteProject(project), style: 'destructive' },
            { text: 'Cancel', style: 'cancel' }
          ]
        );
      }}
    >
      <View style={styles.projectHeader}>
        <Image
          source={{ uri: project.image || 'https://via.placeholder.com/300/0e4e96/FFFFFF?text=Project' }}
          style={styles.projectImage}
        />
        <View style={styles.projectHeaderInfo}>
          <Text style={styles.projectTitle} numberOfLines={2}>{project.title}</Text>
          <Text style={styles.projectClient}>{project.client}</Text>
          <View style={styles.projectMeta}>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(project.status) }]}>
              <Text style={styles.statusText}>{project.status}</Text>
            </View>
            {project.category && (
              <View style={styles.categoryBadge}>
                <Text style={styles.categoryText}>{project.category}</Text>
              </View>
            )}
          </View>
        </View>
      </View>

      <Text style={styles.projectDescription} numberOfLines={3}>
        {project.description}
      </Text>

      <View style={styles.projectFooter}>
        <View style={styles.projectDetails}>
          <View style={styles.projectDetailItem}>
            <Ionicons name="calendar-outline" size={14} color="#64748B" />
            <Text style={styles.projectDetailText}>{project.date}</Text>
          </View>
          {project.budget && (
            <View style={styles.projectDetailItem}>
              <Ionicons name="cash-outline" size={14} color="#64748B" />
              <Text style={styles.projectDetailText}>{project.budget}</Text>
            </View>
          )}
          {project.teamSize && (
            <View style={styles.projectDetailItem}>
              <Ionicons name="people-outline" size={14} color="#64748B" />
              <Text style={styles.projectDetailText}>{project.teamSize}</Text>
            </View>
          )}
        </View>

        <View style={styles.projectActions}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => handleEditProject(project)}
          >
            <Ionicons name="create-outline" size={16} color="#0e4e96" />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => handleDeleteProject(project)}
          >
            <Ionicons name="trash-outline" size={16} color="#EF4444" />
          </TouchableOpacity>
        </View>
      </View>

      {project.technologies && project.technologies.length > 0 && (
        <View style={styles.technologiesContainer}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {project.technologies.slice(0, 3).map((tech, index) => (
              <View key={index} style={styles.technologyTag}>
                <Text style={styles.technologyText}>{tech}</Text>
              </View>
            ))}
            {project.technologies.length > 3 && (
              <View style={styles.technologyTag}>
                <Text style={styles.technologyText}>+{project.technologies.length - 3}</Text>
              </View>
            )}
          </ScrollView>
        </View>
      )}
    </TouchableOpacity>
  );

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Planning': return '#94A3B8';
      case 'In Progress': return '#0e4e96';
      case 'Completed': return '#10B981';
      case 'On Hold': return '#F59E0B';
      case 'Cancelled': return '#EF4444';
      default: return '#64748B';
    }
  };

  // Render filters modal
  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowFilters(false)}
    >
      <View style={styles.filtersModalContainer}>
        <View style={styles.filtersModalContent}>
          <View style={styles.filtersModalHeader}>
            <Text style={styles.filtersModalTitle}>Filters & Sort</Text>
            <TouchableOpacity
              style={styles.filtersModalCloseButton}
              onPress={() => setShowFilters(false)}
            >
              <Ionicons name="close" size={24} color="#0F172A" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.filtersModalBody}>
            {/* Category Filter */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterOptions}>
                  {categories.map((category) => (
                    <TouchableOpacity
                      key={category}
                      style={[
                        styles.filterOption,
                        selectedCategory === category && styles.filterOptionSelected
                      ]}
                      onPress={() => setSelectedCategory(category)}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        selectedCategory === category && styles.filterOptionTextSelected
                      ]}>
                        {category}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Status Filter */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Status</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterOptions}>
                  {statuses.map((status) => (
                    <TouchableOpacity
                      key={status}
                      style={[
                        styles.filterOption,
                        selectedStatus === status && styles.filterOptionSelected
                      ]}
                      onPress={() => setSelectedStatus(status)}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        selectedStatus === status && styles.filterOptionTextSelected
                      ]}>
                        {status}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Sort Options */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <View style={styles.sortOptions}>
                {[
                  { key: 'date', label: 'Date' },
                  { key: 'title', label: 'Title' },
                  { key: 'client', label: 'Client' },
                  { key: 'status', label: 'Status' }
                ].map((option) => (
                  <TouchableOpacity
                    key={option.key}
                    style={[
                      styles.sortOption,
                      sortBy === option.key && styles.sortOptionSelected
                    ]}
                    onPress={() => setSortBy(option.key)}
                  >
                    <Text style={[
                      styles.sortOptionText,
                      sortBy === option.key && styles.sortOptionTextSelected
                    ]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </ScrollView>

          <View style={styles.filtersModalFooter}>
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={() => {
                setSelectedCategory('All');
                setSelectedStatus('All');
                setSortBy('date');
              }}
            >
              <Text style={styles.clearFiltersButtonText}>Clear All</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.applyFiltersButton}
              onPress={() => setShowFilters(false)}
            >
              <Text style={styles.applyFiltersButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Render edit modal
  const renderEditModal = () => (
    <Modal
      visible={showEditModal}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowEditModal(false)}
    >
      <KeyboardAvoidingView
        style={styles.editModalContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.editModalContent}>
          <View style={styles.editModalHeader}>
            <TouchableOpacity
              style={styles.editModalCloseButton}
              onPress={() => setShowEditModal(false)}
            >
              <Ionicons name="close" size={24} color="#0F172A" />
            </TouchableOpacity>
            <Text style={styles.editModalTitle}>
              {editMode === 'add' ? 'Add Project' : 'Edit Project'}
            </Text>
            <TouchableOpacity
              style={styles.editModalSaveButton}
              onPress={handleSaveProject}
            >
              <Text style={styles.editModalSaveButtonText}>Save</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.editModalBody} showsVerticalScrollIndicator={false}>
            <View style={styles.formContainer}>
              {/* Title */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Project Title *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.title && styles.formInputError]}
                  value={formData.title || ''}
                  onChangeText={(text) => setFormData({ ...formData, title: text })}
                  placeholder="Enter project title"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.title && <Text style={styles.formErrorText}>{formErrors.title}</Text>}
              </View>

              {/* Description */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Description *</Text>
                <TextInput
                  style={[styles.formTextArea, formErrors.description && styles.formInputError]}
                  value={formData.description || ''}
                  onChangeText={(text) => setFormData({ ...formData, description: text })}
                  placeholder="Describe your project"
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={4}
                />
                {formErrors.description && <Text style={styles.formErrorText}>{formErrors.description}</Text>}
              </View>

              {/* Client */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Client *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.client && styles.formInputError]}
                  value={formData.client || ''}
                  onChangeText={(text) => setFormData({ ...formData, client: text })}
                  placeholder="Client name"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.client && <Text style={styles.formErrorText}>{formErrors.client}</Text>}
              </View>

              {/* Date Range */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Date Range *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.date && styles.formInputError]}
                  value={formData.date || ''}
                  onChangeText={(text) => setFormData({ ...formData, date: text })}
                  placeholder="e.g., January 2024 - March 2024"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.date && <Text style={styles.formErrorText}>{formErrors.date}</Text>}
              </View>

              {/* Status */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Status *</Text>
                <View style={styles.formPickerContainer}>
                  {['Planning', 'In Progress', 'Completed', 'On Hold', 'Cancelled'].map((status) => (
                    <TouchableOpacity
                      key={status}
                      style={[
                        styles.formPickerOption,
                        formData.status === status && styles.formPickerOptionSelected
                      ]}
                      onPress={() => setFormData({ ...formData, status })}
                    >
                      <Text style={[
                        styles.formPickerOptionText,
                        formData.status === status && styles.formPickerOptionTextSelected
                      ]}>
                        {status}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {formErrors.status && <Text style={styles.formErrorText}>{formErrors.status}</Text>}
              </View>

              {/* Category */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Category</Text>
                <View style={styles.formPickerContainer}>
                  {categories.filter(cat => cat !== 'All').map((category) => (
                    <TouchableOpacity
                      key={category}
                      style={[
                        styles.formPickerOption,
                        formData.category === category && styles.formPickerOptionSelected
                      ]}
                      onPress={() => setFormData({ ...formData, category })}
                    >
                      <Text style={[
                        styles.formPickerOptionText,
                        formData.category === category && styles.formPickerOptionTextSelected
                      ]}>
                        {category}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Budget */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Budget</Text>
                <TextInput
                  style={styles.formInput}
                  value={formData.budget || ''}
                  onChangeText={(text) => setFormData({ ...formData, budget: text })}
                  placeholder="e.g., £250,000"
                  placeholderTextColor="#94A3B8"
                />
              </View>

              {/* Team Size */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Team Size</Text>
                <TextInput
                  style={styles.formInput}
                  value={formData.teamSize || ''}
                  onChangeText={(text) => setFormData({ ...formData, teamSize: text })}
                  placeholder="e.g., 8 members"
                  placeholderTextColor="#94A3B8"
                />
              </View>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // Render loading screen
  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>All Projects</Text>
          <View style={{ width: 32 }} />
        </View>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading projects...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>All Projects</Text>
        <TouchableOpacity
          style={styles.addButton}
          onPress={handleAddProject}
        >
          <Ionicons name="add" size={24} color="#ffffff" />
        </TouchableOpacity>
      </View>

      <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
        {/* Search and Filters */}
        <View style={styles.searchContainer}>
          <View style={styles.searchBar}>
            <Ionicons name="search" size={20} color="#64748B" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search projects..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor="#94A3B8"
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={20} color="#64748B" />
              </TouchableOpacity>
            )}
          </View>
          <TouchableOpacity
            style={styles.filterButton}
            onPress={() => setShowFilters(true)}
          >
            <Ionicons name="filter" size={20} color="#0e4e96" />
          </TouchableOpacity>
        </View>

        {/* Active Filters */}
        {(selectedCategory !== 'All' || selectedStatus !== 'All') && (
          <View style={styles.activeFiltersContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {selectedCategory !== 'All' && (
                <View style={styles.activeFilter}>
                  <Text style={styles.activeFilterText}>{selectedCategory}</Text>
                  <TouchableOpacity onPress={() => setSelectedCategory('All')}>
                    <Ionicons name="close" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                </View>
              )}
              {selectedStatus !== 'All' && (
                <View style={styles.activeFilter}>
                  <Text style={styles.activeFilterText}>{selectedStatus}</Text>
                  <TouchableOpacity onPress={() => setSelectedStatus('All')}>
                    <Ionicons name="close" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                </View>
              )}
            </ScrollView>
          </View>
        )}

        {/* Projects List */}
        <FlatList
          data={filteredProjects}
          renderItem={renderProjectCard}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.projectsList}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="briefcase-outline" size={60} color="#CBD5E1" />
              <Text style={styles.emptyText}>No projects found</Text>
              <Text style={styles.emptySubtext}>
                {searchQuery || selectedCategory !== 'All' || selectedStatus !== 'All'
                  ? 'Try adjusting your search or filters'
                  : 'Add your first project to get started'}
              </Text>
              <TouchableOpacity 
                style={styles.addFirstProjectButton}
                onPress={handleAddProject}
              >
                <Text style={styles.addFirstProjectButtonText}>Add Project</Text>
              </TouchableOpacity>
            </View>
          }
        />
      </Animated.View>

      {/* Modals */}
      {renderFiltersModal()}
      {renderEditModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
    fontWeight: '500',
  },
  content: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#0F172A',
    marginLeft: 12,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeFiltersContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  activeFilter: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0e4e96' + '20',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
  },
  activeFilterText: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
    marginRight: 6,
  },
  projectsList: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  projectCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    marginBottom: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  projectHeader: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  projectImage: {
    width: 60,
    height: 60,
    borderRadius: 12,
    backgroundColor: '#E2E8F0',
    marginRight: 16,
  },
  projectHeaderInfo: {
    flex: 1,
  },
  projectTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  projectClient: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  projectMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 8,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
  },
  categoryBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '500',
    color: '#64748B',
  },
  projectDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 16,
  },
  projectFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  projectDetails: {
    flex: 1,
  },
  projectDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  projectDetailText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 6,
  },
  projectActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  technologiesContainer: {
    marginTop: 12,
  },
  technologyTag: {
    backgroundColor: '#0e4e96' + '20',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 6,
  },
  technologyText: {
    fontSize: 10,
    color: '#0e4e96',
    fontWeight: '500',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  addFirstProjectButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  addFirstProjectButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Filters Modal Styles
  filtersModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  filtersModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.7,
  },
  filtersModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  filtersModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  filtersModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filtersModalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  filterSection: {
    marginVertical: 16,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
  },
  filterOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  filterOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  filterOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  filterOptionTextSelected: {
    color: '#ffffff',
  },
  sortOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  sortOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  sortOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  sortOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  sortOptionTextSelected: {
    color: '#ffffff',
  },
  filtersModalFooter: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  clearFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    marginRight: 8,
  },
  clearFiltersButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  applyFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    marginLeft: 8,
  },
  applyFiltersButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Edit Modal Styles
  editModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  editModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.9,
    minHeight: height * 0.6,
  },
  editModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  editModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  editModalSaveButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  editModalSaveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  editModalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  formContainer: {
    paddingVertical: 20,
  },
  formField: {
    marginBottom: 20,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#ffffff',
  },
  formInputError: {
    borderColor: '#EF4444',
  },
  formTextArea: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#ffffff',
    minHeight: 100,
    textAlignVertical: 'top',
  },
  formErrorText: {
    fontSize: 12,
    color: '#EF4444',
    marginTop: 4,
  },
  formPickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  formPickerOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#ffffff',
  },
  formPickerOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  formPickerOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  formPickerOptionTextSelected: {
    color: '#ffffff',
  },
});
